/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import OptionsBox from "components/ui/OptionsBox";
import kindsOfMomentMappingData from "lazy_components/smart_inquiry/kinds_of_moment_mapping.json";
import Label from "components/ui/Input/Label";

import withAlertModalContext from "./context";
import { dataLayer } from "../../../../lib/analytics";

// We use "other" as it has a list of generic events
const PRIVATE_KINDS_OF_MOMENT = kindsOfMomentMappingData.other.private.map(
  (kindOfMoment) => `${kindOfMoment}_private`,
);
// We use "other" as it has a list of generic events
const COMPANY_KINDS_OF_MOMENT = kindsOfMomentMappingData.other.company.map(
  (kindOfMoment) => `${kindOfMoment}_company`,
);

const KindsOfMoment = ({ context }) => {
  const {
    errors,
    clearError,
    updateData,
    data: { kinds_of_moment: selectedKindsOfMoment },
  } = context;

  const privateKindsOfMoment = useMemo(() => {
    return PRIVATE_KINDS_OF_MOMENT.map((kind_of_moment) => {
      return {
        value: kind_of_moment,
        type: "private",
        label: I18n.t(`kinds_of_moment.${kind_of_moment}`),
      };
    });
  });

  const companyKindsOfMoment = useMemo(() => {
    return COMPANY_KINDS_OF_MOMENT.map((kind_of_moment) => {
      return {
        value: kind_of_moment,
        type: "company",
        label: I18n.t(`kinds_of_moment.${kind_of_moment}`),
      };
    });
  });

  return (
    <div className="b-inquiry-modal__step">
      <Label>{I18n.t("alert_modal.kinds_of_moment.subtitle")}</Label>
      {errors.kinds_of_moment && (
        <div className="b-input__error">{errors.kinds_of_moment}</div>
      )}
      <div className="b-alerts-modal__options-box-container">
        <OptionsBox
          className="b-alerts-modal__options-box"
          title={I18n.t("alert_modal.kinds_of_moment.business_title")}
        >
          <label htmlFor="all_business" key={I18n.t("kinds_of_moment.all")}>
            <input
              key="all_business"
              type="checkbox"
              id="all_business"
              value="all"
              aria-label={I18n.t("kinds_of_moment.all")}
              checked={COMPANY_KINDS_OF_MOMENT.every((k) =>
                selectedKindsOfMoment.includes(k),
              )}
              onChange={(e) => {
                clearError("kinds_of_moment");
                const isChecked = e.target.checked;
                if (isChecked) {
                  updateData({
                    kinds_of_moment: [
                      ...selectedKindsOfMoment,
                      ...COMPANY_KINDS_OF_MOMENT,
                    ],
                  });
                } else {
                  updateData({
                    kinds_of_moment: [
                      ...selectedKindsOfMoment.filter(
                        (k) => !COMPANY_KINDS_OF_MOMENT.includes(k),
                      ),
                    ],
                  });
                }
              }}
            />
            {I18n.t("kinds_of_moment.all")}
          </label>
          <Checkboxes
            kindsOfMoment={companyKindsOfMoment}
            checkedKindsOfMoment={selectedKindsOfMoment}
            onChange={(e) => {
              clearError("kinds_of_moment");
              const item = e.target.value;
              const isChecked = e.target.checked;
              const newChecked = isChecked
                ? [...selectedKindsOfMoment, item]
                : [...selectedKindsOfMoment.filter((k) => k !== item)];
              updateData({
                kinds_of_moment: newChecked,
              });
            }}
          />
        </OptionsBox>
        <OptionsBox
          className="b-alerts-modal__options-box"
          title={I18n.t("alert_modal.kinds_of_moment.private_title")}
        >
          <label htmlFor="all_private" key={I18n.t("kinds_of_moment.all")}>
            <input
              key="all_private"
              type="checkbox"
              id="all_private"
              value="all"
              aria-label={I18n.t("kinds_of_moment.all")}
              checked={PRIVATE_KINDS_OF_MOMENT.every((k) =>
                selectedKindsOfMoment.includes(k),
              )}
              onChange={(e) => {
                clearError("kinds_of_moment");
                const isChecked = e.target.checked;
                if (isChecked) {
                  updateData({
                    kinds_of_moment: [
                      ...selectedKindsOfMoment,
                      ...PRIVATE_KINDS_OF_MOMENT,
                    ],
                  });
                } else {
                  updateData({
                    kinds_of_moment: [
                      ...selectedKindsOfMoment.filter(
                        (k) => !PRIVATE_KINDS_OF_MOMENT.includes(k),
                      ),
                    ],
                  });
                }
              }}
            />
            {I18n.t("kinds_of_moment.all")}
          </label>
          <Checkboxes
            kindsOfMoment={privateKindsOfMoment}
            checkedKindsOfMoment={selectedKindsOfMoment}
            onChange={(e) => {
              clearError("kinds_of_moment");
              const item = e.target.value;
              const isChecked = e.target.checked;
              const newChecked = isChecked
                ? [...selectedKindsOfMoment, item]
                : [...selectedKindsOfMoment.filter((k) => k !== item)];
              updateData({
                kinds_of_moment: newChecked,
              });
            }}
          />
        </OptionsBox>
      </div>
    </div>
  );
};

const Checkboxes = ({ kindsOfMoment, checkedKindsOfMoment, onChange }) => {
  if (!kindsOfMoment) {
    return null;
  }

  return kindsOfMoment.map(({ value, label, type }) => (
    <label htmlFor={`kind_of_moment_${type}_${value}`} key={label}>
      <input
        key={`kind_of_moment_${type}_${value}`}
        type="checkbox"
        id={`kind_of_moment_${type}_${value}`}
        value={value}
        aria-label={value}
        checked={checkedKindsOfMoment.includes(value)}
        onChange={onChange}
      />
      {label}
    </label>
  ));
};

export const validation = (data) => {
  const errors = {};
  if (!data.kinds_of_moment.length) {
    errors.kinds_of_moment = I18n.t("smart_form.errors.please_choose_one");
  }
  if (Object.keys(errors).length > 0) {
    dataLayer.push({
      event: "jobBoardAlertSettingsNextError",
      step: "kinds_of_moment",
    });
  }
  return errors;
};

Checkboxes.propTypes = {
  kindsOfMoment: PropTypes.array.isRequired,
  checkedKindsOfMoment: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  context: PropTypes.shape({
    updateData: PropTypes.func.isRequired,
    data: PropTypes.shape({
      kinds_of_moment: PropTypes.shape({
        personal: PropTypes.object.isRequired,
        company: PropTypes.object.isRequired,
      }).isRequired,
      categories: PropTypes.object.isRequired,
      countries: PropTypes.object.isRequired,
    }).isRequired,
  }),
};

Checkboxes.defaultProps = {
  context: {
    updateData: () => {},
    data: {
      kinds_of_moment: [],
      category: "",
      country_codes: [],
    },
  },
};

export default withAlertModalContext(KindsOfMoment);
